import { ReactNode, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(RelativeTime)

import Card from 'app/frontend/base/components/Card'
import SeasonSelect from 'app/frontend/base/pages/TeamDashboard/subpages/TeamLoot/components/SeasonSelect'
import {
  usePageInfo,
  useRaidPlans,
  useUserRaidPlansIndex,
} from 'app/frontend/base/config/queries'
import Difficulty, {
  difficultiesLabels,
} from 'app/frontend/types/custom/Difficulty'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus } from 'lucide-react'
import TeamPlanCompositionSummary from './TeamPlanCompositionSummary'
import { RaidPlanOwner, createRaidPlan } from 'app/frontend/base/api/raid_plans'

type BeforePlansData = {
  teamPlanCounts: Record<number, number>
  isLoading: boolean
}

type Props = {
  owner: RaidPlanOwner
  basePath: string
  canEdit: boolean
  intro?: ReactNode
  beforePlans?: (data: BeforePlansData) => ReactNode
}

const RaidPlansOverviewContent = ({
  owner,
  basePath,
  canEdit,
  intro,
  beforePlans,
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const {
    data: { currentSeason, currentExpansion },
  } = usePageInfo()
  const { difficulties: availableDifficulties } = useDifficulties()

  const [selectedSeason, setSelectedSeason] = useState(() => {
    const storedSeasonId = Number(localStorage.getItem('plansSeasonId'))
    return (
      currentExpansion.seasons.find((season) => season.id === storedSeasonId) ??
      currentSeason
    )
  })

  const [difficulty, setDifficulty] = useState<Difficulty>(() => {
    const storedDifficulty = localStorage.getItem(
      'plansDifficulty',
    ) as Difficulty | null
    if (storedDifficulty && availableDifficulties.includes(storedDifficulty))
      return storedDifficulty
    return availableDifficulties[0]
  })

  const isUserOwner = owner.ownerType === 'user'
  const { data: userPlansIndex, isLoading: isLoadingUserPlans } =
    useUserRaidPlansIndex(selectedSeason?.id, difficulty, isUserOwner)
  const { data: ownerRaidPlans, isLoading: isLoadingOwnerPlans } = useRaidPlans(
    isUserOwner ? undefined : owner,
    selectedSeason?.id,
    !isUserOwner,
  )
  const isLoadingPlans = isUserOwner ? isLoadingUserPlans : isLoadingOwnerPlans
  const raidPlans = isUserOwner ? userPlansIndex?.raidPlans : ownerRaidPlans
  const teamPlanCounts = userPlansIndex?.teamPlanCounts ?? {}

  const invalidatePlans = async () => {
    await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
    await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
    await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
  }

  const createMutation = useMutation({
    mutationFn: async (encounterId: number) => {
      return await createRaidPlan(owner, {
        encounter_id: encounterId,
        difficulty,
        name: t('dashboard.team.plans.default-name'),
        timeline: { actors: [], phase_durations: {} },
      })
    },
    onSuccess: async (plan) => {
      await invalidatePlans()
      navigate(`${basePath}/${plan.id}`)
    },
  })

  const plansByEncounter = useMemo(() => {
    return (raidPlans ?? [])
      .filter((plan) => plan.difficulty === difficulty)
      .reduce<Record<number, RaidPlan[]>>((acc, plan) => {
        if (!acc[plan.encounterId]) acc[plan.encounterId] = []
        acc[plan.encounterId].push(plan)
        return acc
      }, {})
  }, [difficulty, raidPlans])

  const encounters = useMemo(() => {
    return selectedSeason.instances
      .flatMap((instance) => instance.encounters)
      .sort(
        (a, b) =>
          a.instanceId - b.instanceId || a.defaultOrder - b.defaultOrder,
      )
  }, [selectedSeason])

  const onSeasonChange = (value?: string) => {
    const season = currentExpansion.seasons.find(
      (s) => s.id.toString() === value,
    )
    if (!season) return
    localStorage.setItem('plansSeasonId', season.id.toString())
    setSelectedSeason(season)
  }

  return (
    <div className='space-y-4'>
      <h2 className='flex justify-between gap-3 flex-wrap items-center'>
        <span>{t('dashboard.team.plans.title')}</span>
        <div className='flex items-center gap-2 flex-wrap'>
          <SeasonSelect
            className='w-52'
            value={selectedSeason?.id?.toString()}
            setValue={onSeasonChange}
          />
          <Tabs
            value={difficulty}
            onValueChange={(value) => {
              const next = value as Difficulty
              setDifficulty(next)
              localStorage.setItem('plansDifficulty', next)
            }}
          >
            <TabsList>
              {availableDifficulties.map((option) => (
                <TabsTrigger key={option} value={option}>
                  {difficultiesLabels[option]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </h2>

      {intro}
      {beforePlans?.({ teamPlanCounts, isLoading: isLoadingPlans })}

      <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {encounters.map((encounter) => {
          const encounterPlans = plansByEncounter[encounter.id] ?? []

          if (isLoadingPlans) {
            return <Skeleton key={encounter.id} className='h-[102px]' />
          }

          return (
            <Card key={encounter.id} className='p-4'>
              <div className='flex items-start justify-between gap-3'>
                <div className='flex items-center gap-3 min-w-0'>
                  {encounter.thumbnailUrl ? (
                    <img
                      className='w-10 h-10 rounded-md border border-border shrink-0'
                      src={encounter.thumbnailUrl}
                    />
                  ) : (
                    <div className='w-10 h-10 rounded-md border border-border bg-muted' />
                  )}
                  <h4 className='truncate'>{encounter.name}</h4>
                </div>
                {canEdit && (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => createMutation.mutate(encounter.id)}
                  >
                    <Plus className='h-4 w-4' />
                  </Button>
                )}
              </div>

              <div className='mt-2 flex flex-col gap-2'>
                {encounterPlans.length === 0 ? (
                  <span className='text-sm text-muted-foreground'>
                    {t('dashboard.team.plans.empty-encounter')}
                  </span>
                ) : (
                  encounterPlans.map((plan) => (
                    <Link to={`${basePath}/${plan.id}`} key={plan.id}>
                      <Card
                        key={plan.id}
                        className='p-3 hover:border-primary transition-colors'
                      >
                        <div className='flex items-center justify-between gap-2'>
                          <div className='truncate font-medium'>
                            {plan.name}
                          </div>
                          <div className='text-xs text-muted-foreground whitespace-nowrap'>
                            Created {dayjs(plan.createdAt).fromNow()}
                          </div>
                        </div>
                        <TeamPlanCompositionSummary
                          timeline={plan.timeline}
                          className='mt-2'
                        />
                      </Card>
                    </Link>
                  ))
                )}
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default RaidPlansOverviewContent
